import { useEffect, useMemo } from 'react';
import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import styles from './styles.module.scss';

import {
  convertKilometersToMeters,
  convertMetersToKilometers,
  convertMoneyInputFormatToFloatWithNegative,
} from '@/Utils/FnUtils';

import { queryClient } from '@/_config/lib/react-query/query-client';
import { Breadcrumb } from '@/_components/breadcrumb';
import { FareForm } from './components/fare-form/fare-form';
import { FARE_OF_TYPE } from './types';
import type { District, FranchiseType, TypeFranchiseForm } from './types';
import { franchiseFormSchema } from './constants';
import type { PlaceFareDataParams } from './api/post-place-fare';
import { useCreatePlaceFare } from './api/post-place-fare';
import { useUpdatePlaceFare } from './api/put-place-fare';
import { GET_PLACE_FARE } from './api/get-place-fare';

interface PlaceFareFormProps {
  placeInfo?: FranchiseType;
}

function PlaceFareForm({ placeInfo }: PlaceFareFormProps) {
  const navigate = useNavigate();

  const isEditingFare = placeInfo && placeInfo.entregaBaseMts !== undefined;

  const { mutate: createFare } = useCreatePlaceFare();
  const { mutate: updateFare } = useUpdatePlaceFare();

  const placeName = isEditingFare ? 'Atualizar tarifa' : 'Criar tarifa';

  const isFaresPage = location.pathname.includes('/tarifas/');

  const backButtonName = isFaresPage ? 'Tarifas' : 'Detalhes';

  const neighborhoodsWithFormattedValues = useMemo(
    () =>
      (placeInfo?.neighborhoods || []).map(neighborhood => ({
        ...neighborhood,
        logFee: neighborhood.logFee
          ? convertMoneyInputFormatToFloatWithNegative(
              String(neighborhood.logFee.toFixed(2)),
            )
          : 0,
        deliveryValue: neighborhood.deliveryValue
          ? convertMoneyInputFormatToFloatWithNegative(
              String(neighborhood.deliveryValue.toFixed(2)),
            )
          : 0,
        latitude: neighborhood.location?.latitude,
        longitude: neighborhood.location?.longitude,
        isActive: !!neighborhood.isActive,
      })),
    [placeInfo?.neighborhoods],
  );

  const formDefaultValues = useMemo<DefaultValues<TypeFranchiseForm>>(
    () => ({
      cityId: placeInfo?.cityId || '',
      ufInitials: placeInfo?.uf || '',
      placeId: placeInfo?._id,
      establishmentName: placeInfo?.establishmentName || '',
      cityName: placeInfo?.cityName || '',

      valorEntregaBase: placeInfo?.valorEntregaBase
        ? convertMoneyInputFormatToFloatWithNegative(
            String(placeInfo.valorEntregaBase.toFixed(2)),
          )
        : 0,
      taxaEntregaBase: placeInfo?.taxaEntregaBase
        ? convertMoneyInputFormatToFloatWithNegative(
            String(placeInfo.taxaEntregaBase.toFixed(2)),
          )
        : 0,
      valorKmAdicional: placeInfo?.valorKmAdicional
        ? convertMoneyInputFormatToFloatWithNegative(
            String(placeInfo.valorKmAdicional.toFixed(2)),
          )
        : 0,
      taxaEntregaKmAdicional: placeInfo?.taxaEntregaKmAdicional
        ? convertMoneyInputFormatToFloatWithNegative(
            String(placeInfo.taxaEntregaKmAdicional.toFixed(2)),
          )
        : 0,
      qtdMaxEntregaGroup: placeInfo?.qtdMaxEntregaGroup || 0,

      entregaBaseKm: placeInfo?.entregaBaseMts
        ? convertMetersToKilometers(placeInfo.entregaBaseMts)
        : 0,
      raioMaxDistance: placeInfo?.raioMaxDistance
        ? convertMetersToKilometers(placeInfo.raioMaxDistance)
        : 0,
      maxDistanceDropsToGroup: placeInfo?.maxDistanceDropsToGroup
        ? convertMetersToKilometers(placeInfo.maxDistanceDropsToGroup)
        : 0,
      minDistanceCloseTo: placeInfo?.minDistanceCloseTo
        ? convertMetersToKilometers(placeInfo.minDistanceCloseTo)
        : 0,
      requiredNearPlaceToConfirm: placeInfo?.requiredNearPlaceToConfirm
        ? placeInfo?.requiredNearPlaceToConfirm
        : false,
      requiredReturnToConfirmPayMethods:
        placeInfo?.requiredReturnToConfirmPayMethods || [],
      fareOfType: placeInfo?.fareOfType || FARE_OF_TYPE.KM,
      bairros: neighborhoodsWithFormattedValues,
    }),
    [neighborhoodsWithFormattedValues, placeInfo],
  );

  const methods = useForm<TypeFranchiseForm>({
    resolver: yupResolver(franchiseFormSchema),
    defaultValues: formDefaultValues,
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(formDefaultValues);
  }, [formDefaultValues, reset]);

  const handleSubmitForm: SubmitHandler<TypeFranchiseForm> = async data => {
    const {
      bairros,
      entregaBaseKm,
      fareOfType,
      raioMaxDistance,
      maxDistanceDropsToGroup,
      minDistanceCloseTo,
      ...newFranchiseNecessaryValues
    } = data;

    delete newFranchiseNecessaryValues.placeId;
    delete newFranchiseNecessaryValues.cityName;
    delete newFranchiseNecessaryValues.cityId;
    delete newFranchiseNecessaryValues.establishmentName;
    delete newFranchiseNecessaryValues.ufInitials;

    const actualNeighborhoods = isEditingFare
      ? bairros?.map(({ _id, logFee, deliveryValue }: District) => ({
          _id,
          logFee,
          deliveryValue,
        }))
      : bairros?.map(
          ({
            _id,
            logFee,
            deliveryValue,
            generalCityId,
            generalNeighborhoodId,
            name,
            location,
          }: District) => ({
            logFee,
            deliveryValue,
            _id,
            generalCityId,
            generalNeighborhoodId,
            name,
            location,
          }),
        );

    const actualFareOfType = fareOfType || FARE_OF_TYPE.KM;

    const hasNeighborhoods =
      !!actualNeighborhoods && actualNeighborhoods?.length > 0;

    const placeId = data.placeId || '';

    const fareData: PlaceFareDataParams = {
      ...newFranchiseNecessaryValues,
      fareOfType: actualFareOfType
        ? (actualFareOfType as FARE_OF_TYPE)
        : FARE_OF_TYPE.KM,
      entregaBaseMts: entregaBaseKm
        ? convertKilometersToMeters(entregaBaseKm)
        : undefined,
      raioMaxDistance: raioMaxDistance
        ? convertKilometersToMeters(raioMaxDistance)
        : undefined,
      maxDistanceDropsToGroup: maxDistanceDropsToGroup
        ? convertKilometersToMeters(maxDistanceDropsToGroup)
        : undefined,
      minDistanceCloseTo: minDistanceCloseTo
        ? convertKilometersToMeters(minDistanceCloseTo)
        : undefined,
      neighborhoods: hasNeighborhoods ? actualNeighborhoods : undefined,
      localizacao: placeInfo?.localizacao,
    };

    const functionToExecute = isEditingFare ? updateFare : createFare;

    functionToExecute(
      { placeId, fareData: { ...fareData } },
      {
        onSettled: () => {
          queryClient.invalidateQueries([GET_PLACE_FARE, { placeId }]);
          navigate(-1);
        },
      },
    );
  };

  return (
    <div className="container">
      <div className={styles.mainContainer}>
        <Breadcrumb showBackButtonIcon={false} backButtonLabel={backButtonName}>
          <Link data-actived className="text-gray" relative="path" to="">
            {placeName}
          </Link>
        </Breadcrumb>
        <FormProvider {...methods}>
          <FareForm
            handleSubmit={handleSubmit(handleSubmitForm)}
            isEditing={isEditingFare}
            fareType="Place"
            importPlacesCityId={placeInfo?.cityId || ''}
          />
        </FormProvider>
      </div>
    </div>
  );
}

export default PlaceFareForm;
